import { getData } from '@/utils/getData'
import { format, add } from 'date-fns'
import { RewardType } from '@/enums/RewardType'
import { Vertical } from '@/enums/Vertical'
import { CampaignStatus } from '@/enums/CampaignStatus'
import { CampaignDepartment } from '@/enums/CampaignDepartment'
import { MarkupType } from '@/enums/MarkupType'
import { TrafficPropertyType } from '@/enums/TrafficPropertyType'
import { showErrorMessageSnackBar } from '@/utils'
import { useStorage } from 'vue3-storage'
import { pick } from 'lodash'

export type Filters = {
  affiliateId?: number
  offerId?: number
  campaignId?: number
  landingId?: number
  preLandingId?: number
  geoIds?: number[]
  geoId?: number
  markupType?: MarkupType
  dateFrom?: number | string
  dateTo?: number | string
  trafficProperty?: TrafficPropertyType
  vertical?: Vertical
  advertiserId?: number
  categoryId?: number
  rewardType?: RewardType
  groupBy?: string
  groupBy2?: string
  groupBy3?: string
}

export type ReadOptions = {
  filters: FiltersType
  groupBy?: GroupBy
  orderBy?: OrderBy
  limit?: number
  offset?: number
}

export type GroupByOptions = Array<{
  id: string
  name: string
  field: string
}>

export type FiltersType = {
  offerId?: number
  campaignId?: number
  landingId?: number
  preLandingId?: number
  geoIds?: number[]
  geoId?: number
  status?: CampaignStatus
  markupType?: number
  dateFrom?: number | string
  dateTo?: number | string
  groupBy?: string
}

type GroupBy = string[]
type OrderBy = { [key: string]: string }
export type Row = { [key: string]: any }
export type Column = { [key: string]: any }

type Pagination = {
  page: number
  rowsNumber: number
  rowsPerPage: number
  sortBy: string
  descending: boolean
}
export type Table = {
  loading: boolean
  rows: string[]
  pagination: Pagination
  filters?: FiltersType
  onRequest: any
}

export type TableRequestProps = {
  pagination: Pagination
  row?: Row
}

export async function request(
  props: TableRequestProps,
  ref: any,
  url: string,
  withoutPagination = false,
) {
  const storage = useStorage()
  storage.setStorageSync(
    'cpaFilters',
    pick(ref.filters, [
      'groupBy',
      'groupBy2',
      'groupBy3',
      'dateFrom',
      'dateTo',
      'department',
      'campaignStatus',
      'affiliateId',
    ]),
  )
  const { page, rowsPerPage, sortBy, descending } = props.pagination
  ref.loading = true
  const limit = withoutPagination
    ? 0
    : rowsPerPage === 0
    ? ref.pagination.rowsNumber
    : rowsPerPage
  const offset = (page - 1) * rowsPerPage
  if (!ref.filters?.groupBy) {
    if (!ref.filters) {
      ref.filters = getDefaultFilters(true)
    }
    ref.filters.groupBy = 'date'
  }
  const orderBy = {
    [sortBy !== 'groupByField'
      ? sortBy
      : String(ref.filters?.groupBy)]: descending ? 'DESC' : 'ASC',
  }
  try {
    ref.rows = await getData(
      { filters: ref.filters || {}, limit, offset, orderBy },
      url,
    )
  } catch (e) {
    showErrorMessageSnackBar('Произошла ошибка запроса.')
  }
  if (!ref.pagination) {
    ref.pagination = getPagination('groupByField', 30)
  }
  ref.pagination.rowsNumber = (ref.rows.length
    ? ((ref.rows[0] as unknown) as Row).total
    : ref.pagination.rowsNumber) as number
  ref.pagination.page = page
  ref.pagination.rowsPerPage = withoutPagination
    ? ref.pagination.rowsNumber
    : rowsPerPage
  ref.pagination.sortBy = sortBy
  ref.pagination.descending = descending
  ref.loading = false
  return ref.rows
}

export function getPagination(
  sortBy: string,
  rowsPerPage: number,
  descending = true,
) {
  return {
    sortBy,
    descending,
    page: 1,
    rowsPerPage,
    rowsNumber: 100,
  }
}

export function getDefaultFilters(fullReset = false): any {
  const date = new Date()
  const today = new Date()
  const dateTo = format(date, 'yyyy/MM/dd')
  const dateFrom = format(today, 'yyyy/MM/dd')
  const storage = useStorage()
  const defaultFilters = {
    groupBy: 'date',
    dateFrom,
    dateTo,
  }
  // проверка истечения сессии
  const expires = storage.getStorageSync('cpaExpires')
  if (!expires) {
    storage.setStorageSync('cpaExpires', add(new Date(), { hours: 8 }))
  } else if (new Date(expires).getTime() < new Date().getTime()) {
    storage.removeStorageSync('cpaFilters')
    storage.setStorageSync('cpaExpires', add(new Date(), { hours: 8 }))
  }

  const cpaFilters = storage.getStorageSync('cpaFilters')
  if (cpaFilters && !fullReset) {
    return cpaFilters
  } else {
    storage.setStorageSync('cpaFilters', defaultFilters)
  }

  return defaultFilters
}

export const getTableProperties = (
  sortBy: string,
  rowsPerPage = 10,
  descending = true,
) => ({
  rows: [],
  loading: true,
  pagination: getPagination(sortBy, rowsPerPage, descending),
})

export function getGroupByField(
  key: 'id' | 'name' | 'field',
  row: Row,
  groupByOptions: GroupByOptions,
  filters: Filters,
): string | undefined {
  for (const option of groupByOptions) {
    if (option.id !== filters.groupBy) {
      continue
    }
    const fieldName = option[key]
    if (key === 'field') {
      switch (fieldName) {
        case 'rewardType':
          return RewardType[Number(row[fieldName])]
        case 'vertical':
          return Vertical[Number(row[fieldName])]
        case 'department':
          return CampaignDepartment[Number(row[fieldName])]
        default:
          return row[fieldName]
      }
    }
    return fieldName
  }
}

export function getGroupBy2Field(
  key: 'id' | 'name' | 'field',
  row: Row,
  groupByOptions: GroupByOptions,
  filters: Filters,
): string | undefined {
  for (const option of groupByOptions) {
    if (option.id !== filters.groupBy2) {
      continue
    }
    const fieldName = option[key]
    if (key === 'field') {
      switch (fieldName) {
        case 'rewardType':
          return RewardType[Number(row[fieldName])]
        case 'vertical':
          return Vertical[Number(row[fieldName])]
        case 'department':
          return CampaignDepartment[Number(row[fieldName])]
        default:
          return row[fieldName]
      }
    }
    return fieldName
  }
}

export function getGroupBy3Field(
  key: 'id' | 'name' | 'field',
  row: Row,
  groupByOptions: GroupByOptions,
  filters: Filters,
): string | undefined {
  for (const option of groupByOptions) {
    if (option.id !== filters.groupBy3) {
      continue
    }
    const fieldName = option[key]
    if (key === 'field') {
      switch (fieldName) {
        case 'rewardType':
          return RewardType[Number(row[fieldName])]
        case 'vertical':
          return Vertical[Number(row[fieldName])]
        case 'department':
          return CampaignDepartment[Number(row[fieldName])]
        default:
          return row[fieldName]
      }
    }
    return fieldName
  }
}
